import React, { useEffect } from 'react';
import { Container, Card, Button, Table } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEOHead from '../components/SEO/SEOHead';
import { MultipleStructuredData } from '../components/SEO/StructuredData';
import { getGuideBySlug, GUIDE_ARTICLES, getGuideSlug } from '../guides/registry';

const GuideArticlePage = () => {
  const { slugPath } = useParams();
  const slug = `/guides/${slugPath}`;
  const article = getGuideBySlug(slug);
  const { t, i18n } = useTranslation('guides');
  const lang = i18n.language?.startsWith('en') ? 'en' : 'fr';

  useEffect(() => {
    if (!article) return;
    if (slug === article.slugEn && !i18n.language?.startsWith('en')) {
      i18n.changeLanguage('en');
    } else if (slug === article.slug && i18n.language?.startsWith('en')) {
      i18n.changeLanguage('fr');
    }
  }, [article, slug, i18n]);

  if (!article) {
    return (
      <>
        <SEOHead
          title={t('common.notFound')}
          description={t('common.notFound')}
          canonical="/"
          noindex
        />
      <Container className="py-5 text-center">
        <h1>{t('common.notFound')}</h1>
        <Button as={Link} to="/" variant="primary" className="mt-3">
          {t('common.backHome')}
        </Button>
      </Container>
      </>
    );
  }

  const baseKey = `articles.${article.id}`;
  const canonicalPath = getGuideSlug(article, lang);
  const canonical = `https://assistance.leagora.io${canonicalPath}`;

  const faqRaw = t(`${baseKey}.faq`, { returnObjects: true });
  const faqItems = Array.isArray(faqRaw) ? faqRaw : [];
  const sectionsRaw = t(`${baseKey}.sections`, { returnObjects: true });
  const sections = Array.isArray(sectionsRaw) ? sectionsRaw : [];
  const hasComparison = i18n.exists(`${baseKey}.comparison.title`);
  const comparison = hasComparison ? t(`${baseKey}.comparison`, { returnObjects: true }) : null;
  const hasHowTo = i18n.exists(`${baseKey}.howTo.steps`);
  const howToSteps = hasHowTo ? t(`${baseKey}.howTo.steps`, { returnObjects: true }) : null;
  const hasChecklist = i18n.exists(`${baseKey}.checklist.items`);
  const checklist = hasChecklist ? t(`${baseKey}.checklist`, { returnObjects: true }) : null;
  const featureRaw = t(`${baseKey}.features.items`, { returnObjects: true });
  const featureItems = Array.isArray(featureRaw) ? featureRaw : [];

  const schemas = [{
    type: 'article',
    data: {
      headline: t(`${baseKey}.meta.title`),
      description: t(`${baseKey}.meta.description`),
      url: canonical,
      datePublished: article.published,
      dateModified: article.published,
      authorName: t('common.authorName'),
      authorUrl: 'https://leagora.io',
    },
  }, {
    type: 'breadcrumbList',
    data: {
      items: [
        { name: t('common.backHome'), url: 'https://assistance.leagora.io/' },
        { name: t('common.guidesIndex'), url: 'https://assistance.leagora.io/guides' },
        { name: t(`${baseKey}.h1`), url: canonical },
      ],
    },
  }];

  if (faqItems.length > 0) {
    schemas.push({
      type: 'faqPage',
      data: {
        faqItems: faqItems.map((item) => ({
          question: item.q,
          answer: item.a,
        })),
      },
    });
  }

  if (howToSteps?.length > 0) {
    schemas.push({
      type: 'howTo',
      data: {
        name: t(`${baseKey}.howTo.name`),
        description: t(`${baseKey}.howTo.description`),
        steps: howToSteps.map((step) => ({
          name: step.name,
          text: step.text,
        })),
      },
    });
  }

  const relatedArticles = (article.related || [])
    .map((id) => GUIDE_ARTICLES.find((a) => a.id === id))
    .filter(Boolean);

  return (
    <>
      <SEOHead
        title={t(`${baseKey}.meta.title`)}
        description={t(`${baseKey}.meta.description`)}
        keywords={t(`${baseKey}.meta.keywords`)}
        canonical={canonicalPath}
        alternateUrls={{ fr: article.slug, en: article.slugEn }}
        ogType="article"
      />
      <MultipleStructuredData schemas={schemas} />

      <Container className="py-5 guide-article" style={{ maxWidth: '900px' }}>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 small">
              <li className="breadcrumb-item">
                <Link to="/">{t('common.backHome')}</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/guides">{t('common.guidesIndex')}</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                {t(`${baseKey}.h1`)}
              </li>
            </ol>
          </nav>
          <Button as={Link} to="/" variant="outline-primary" size="sm">
            {t('common.backHome')}
          </Button>
        </div>

        <article>
          <header className="mb-4">
            <h1 className="h2 fw-bold">{t(`${baseKey}.h1`)}</h1>
            <p className="text-muted small mb-1">
              {t('common.updated')}: {article.published}
            </p>
            <p className="text-muted small mb-0">
              {t('common.authorBy')} <strong>{t('common.authorName')}</strong> — {t('common.authorBio')}
            </p>
          </header>

          <Card className="shadow-sm border-0 mb-4 bg-light">
            <Card.Body className="p-4">
              <p className="mb-0 fw-medium">{t(`${baseKey}.quickAnswer`)}</p>
            </Card.Body>
          </Card>

          <p className="lead">{t(`${baseKey}.intro`)}</p>

          {sections.map((section, index) => (
            <section key={index} className="mt-4">
              <h2 className="h4 fw-bold">{section.title}</h2>
              {section.paragraphs?.map((p, i) => (
                <p key={i} className="text-secondary">{p}</p>
              ))}
              {section.list && (
                <ul className="text-secondary">
                  {section.list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}
            </section>
          ))}

          {comparison?.rows && (
            <section className="mt-4">
              <h2 className="h4 fw-bold">{comparison.title}</h2>
              <div className="table-responsive">
                <Table striped bordered hover size="sm" className="mt-3">
                  <thead>
                    <tr>
                      {comparison.headers.map((h, i) => (
                        <th key={i}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.rows.map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </section>
          )}

          {howToSteps?.length > 0 && (
            <section className="mt-4">
              <h2 className="h4 fw-bold">{t(`${baseKey}.howTo.title`)}</h2>
              <ol className="text-secondary">
                {howToSteps.map((step, i) => (
                  <li key={i} className="mb-3">
                    <strong>{step.name}</strong>
                    <p className="mb-0 mt-1">{step.text}</p>
                  </li>
                ))}
              </ol>
            </section>
          )}

          {checklist?.items && (
            <section className="mt-4">
              <h2 className="h4 fw-bold">{checklist.title}</h2>
              <ul className="list-unstyled">
                {checklist.items.map((item, i) => (
                  <li key={i} className="mb-2">
                    <span className="text-success me-2">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="mt-4">
            <h2 className="h4 fw-bold">{t('common.featuresTitle')}</h2>
            <p className="text-secondary">{t(`${baseKey}.features.intro`)}</p>
            <ul className="text-secondary">
              {featureItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="mt-4">
            <h2 className="h4 fw-bold">{t('common.resultsTitle')}</h2>
            <ul className="text-secondary">
              <li><strong>{t('common.cost')} :</strong> {t(`${baseKey}.results.cost`)}</li>
              <li><strong>{t('common.travel')} :</strong> {t(`${baseKey}.results.travel`)}</li>
              <li><strong>{t('common.satisfaction')} :</strong> {t(`${baseKey}.results.satisfaction`)}</li>
            </ul>
          </section>

          {faqItems.length > 0 && (
            <section className="mt-4">
              <h2 className="h4 fw-bold">{t('common.faqTitle')}</h2>
              {faqItems.map((item, i) => (
                <div key={i} className="mb-3">
                  <h3 className="h6 fw-bold">{item.q}</h3>
                  <p className="text-secondary mb-0">{item.a}</p>
                </div>
              ))}
            </section>
          )}

          <section className="mt-5 p-4 bg-primary bg-opacity-10 rounded">
            <p className="mb-3">{t(`${baseKey}.conclusion`)}</p>
            <Button as={Link} to="/register" variant="primary" size="lg">
              {t('common.tryFree')}
            </Button>
            <p className="small text-muted mt-2 mb-0">{t('common.tryFreeDesc')}</p>
          </section>

          {relatedArticles.length > 0 && (
            <section className="mt-5">
              <h2 className="h5 fw-bold">{t('common.relatedGuides')}</h2>
              <ul>
                {relatedArticles.map((related) => (
                  <li key={related.id}>
                    <Link to={getGuideSlug(related, lang)}>
                      {t(`articles.${related.id}.h1`)}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </article>
      </Container>
    </>
  );
};

export default GuideArticlePage;
